import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { fetchEntitlement } from "@/lib/api";

/**
 * Global entitlement state — refreshed on load, after login/logout, and
 * after payments complete. Consumers use `refresh()` to force a re-check.
 *
 * Shape mirrors the backend `GET /api/me/entitlement`:
 *   { is_premium, is_authenticated, plays_used_today, plays_limit, plays_remaining }
 */
const EntitlementContext = createContext({
  entitlement: null,
  loading: true,
  refresh: async () => {},
});

export function EntitlementProvider({ children }) {
  const [entitlement, setEntitlement] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const data = await fetchEntitlement();
      setEntitlement(data);
      return data;
    } catch {
      setEntitlement(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <EntitlementContext.Provider value={{ entitlement, loading, refresh }}>
      {children}
    </EntitlementContext.Provider>
  );
}

export function useEntitlement() {
  return useContext(EntitlementContext);
}
